import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { exportReportToPDF, exportReportToCSV } from '../../../utils/exportUtils';

const ExportReportControls = ({ tasks, familyMembers, period, dateRange, stats }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await exportReportToPDF(tasks, familyMembers, period, dateRange, stats);
      setShowMenu(false);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCSV = () => {
    setIsExporting(true);
    try {
      exportReportToCSV(tasks, familyMembers, dateRange);
      setShowMenu(false);
    } catch (error) {
      console.error('CSV export failed:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleEmailReport = () => {
    setShowMenu(false);
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    if (!emailAddress || !emailAddress?.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    setIsExporting(true);
    try {
      // Simulate email sending
      setTimeout(() => {
        alert(`Report sent to ${emailAddress}`);
        setShowEmailModal(false);
        setEmailAddress('');
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('Email send failed:', error);
      alert('Failed to send email. Please try again.');
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="relative">
        <Button
          variant="default"
          iconName="download"
          onClick={() => setShowMenu(!showMenu)}
          disabled={isExporting}
          loading={isExporting}
        >
          Export Report
        </Button>

        {showMenu && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 mt-2 w-56 bg-popover rounded-lg shadow-warm-lg border border-border z-50">
              <div className="py-2">
                <button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-accent/10 flex items-center gap-3 transition-colors disabled:opacity-50"
                >
                  <Icon name="file-text" size={18} className="text-error" />
                  <div>
                    <div className="font-medium text-foreground">Export as PDF</div>
                    <div className="text-xs text-muted-foreground">Detailed report with charts</div>
                  </div>
                </button>
                
                <button
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-accent/10 flex items-center gap-3 transition-colors disabled:opacity-50"
                >
                  <Icon name="table" size={18} className="text-success" />
                  <div>
                    <div className="font-medium text-foreground">Export as CSV</div>
                    <div className="text-xs text-muted-foreground">Raw data for analysis</div>
                  </div>
                </button>

                <div className="my-2 border-t border-border" />

                <button
                  onClick={handleEmailReport}
                  disabled={isExporting}
                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-accent/10 flex items-center gap-3 transition-colors disabled:opacity-50"
                >
                  <Icon name="mail" size={18} className="text-primary" />
                  <div>
                    <div className="font-medium text-foreground">Send via Email</div>
                    <div className="text-xs text-muted-foreground">Email report to family</div>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
            onClick={() => setShowEmailModal(false)}
          >
            <div 
              className="bg-popover rounded-xl shadow-warm-xl max-w-md w-full p-6"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="mail" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Email Report</h3>
                  <p className="text-sm text-muted-foreground">Send report to email address</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e?.target?.value)}
                  placeholder="family@example.com"
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  disabled={isExporting}
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-3 mb-4">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex items-center gap-2">
                    <Icon name="calendar" size={14} />
                    <span>Period: {dateRange}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="list-checks" size={14} />
                    <span>Tasks: {stats?.total} ({stats?.completed} completed)</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailModal(false)}
                  fullWidth
                  disabled={isExporting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSendEmail}
                  fullWidth
                  loading={isExporting}
                  disabled={isExporting || !emailAddress}
                  iconName="send"
                >
                  Send Report
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ExportReportControls;